/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { FC, useState } from 'react';
import * as clipboard from 'clipboard-polyfill';
import SVGBurgerIcon from '@assets/SVGBurgerIcon';
import SVGHelpCircle from '@assets/SVGHelpCircle';
import SVGGiftIcon from '@assets/SVGGiftIcon';
import RenderIf from '@components/RenderIf';
import SVGTwitterIcon from '@assets/SVGTwitterIcon';
import SVGLinkedinIcon from '@assets/SVGLinkedinIcon';
import SVGEmailIcon from '@assets/SVGEmailIcon';
import SVGFbIcon from '@assets/SVGFbIcon';
import SVGCloseBurguer from '@assets/SVGCloseBurguer';
import SVGExternalLinkIcon from '@assets/SVGExternalLinkIcon';
import {
  Button,
  IconButton,
  Collapse,
  useTheme,
  useMediaQuery,
  Grid,
  Divider,
  Typography,
} from '@material-ui/core';
import useTopToastNotification from '@hooks/useTopToastNotification';
import SVGCopyClipboard2 from '@assets/SVGCopyClipboard2';
import {
  shareLinkOnFacebook,
  shareLinkOnLinkedIn,
  shareLinkOnMail,
  shareLinkOnTwitter,
  redirectToSendspark,
} from '@utils/segmentHelpers';
import Modal from '@components/VideoScriptGenerator/Modal';
import Faq from '@components/VideoScriptGenerator/FAQ';
import { SvgLogoColored2 } from '@assets/SvgLogoColored2';
import { SVGChat } from '@assets/SVGChat';
import SVGRedirect from '@assets/SVGRedirect';
import { EnvConfig } from '@pages/video-script-generator';
import useStyles from './styles';

interface LayoutProps {
  children: React.ReactNode;
  env: EnvConfig;
}

const Layout: FC<LayoutProps> = ({ children, env }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isLaptop = useMediaQuery(theme.breakpoints.up('md'));
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isOpenFAQModal, setIsOpenFAQModal] = useState<boolean>(false);
  const [isOpenHamburguerMenu, setIsOpenHamburguerMenu] = useState<boolean>(
    false
  );
  const { notification } = useTopToastNotification();

  const intercomHelp = () => {
    (window as any)?.Intercom('show');
  };

  const copyToClipboard = () => {
    clipboard.writeText('AISCRIPTSPARK50');

    notification('Gift code has been copied!');
  };

  return (
    <div className={classes.layout}>
      <div className={classes.container}>
        <Grid container className={classes.header} alignItems="center">
          <Grid container className={classes.logo} item xs={3} md={6} alignItems="center" wrap="nowrap">
            <SvgLogoColored2 />
            <RenderIf condition={!isMobile}>
              <>
                <Divider orientation="vertical" />
                <Typography style={{ whiteSpace: 'nowrap' }}>
                  Video Messaging with Superpowers for Sales
                </Typography>
              </>
            </RenderIf>
          </Grid>
          <Grid container item xs={9} md={6} justify="flex-end">
            <RenderIf condition={!isMobile || isTablet}>
              <div className={classes.sendsparkRedirect}>
                <Button variant="text" onClick={() => redirectToSendspark(false, env)}>
                  Go to Sendspark.com
                  <SVGRedirect />
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => redirectToSendspark(true, env)}
                >
                  Sign up for Sendspark
                </Button>
              </div>
            </RenderIf>
            <RenderIf condition={isMobile && !isTablet}>
              <IconButton
                onClick={() => setIsOpenHamburguerMenu(!isOpenHamburguerMenu)}
              >
                <RenderIf condition={isOpenHamburguerMenu}>
                  <SVGCloseBurguer color={theme.palette.common.black} />
                </RenderIf>
                <RenderIf condition={!isOpenHamburguerMenu}>
                  <SVGBurgerIcon color={theme.palette.common.black} />
                </RenderIf>
              </IconButton>
            </RenderIf>
          </Grid>
        </Grid>
        <div className={classes.contentContainer}>
          <Collapse in={isOpenHamburguerMenu && isMobile && !isTablet}>
            <Grid container direction="column">
              <Button
                className={classes.hamburguerButton}
                variant="text"
                onClick={() => setIsOpenFAQModal(true)}
                fullWidth
              >
                <div className={classes.btnBackground}>
                  <SVGHelpCircle />
                </div>
                FAQ
              </Button>
              <Button
                className={classes.hamburguerButton}
                variant="text"
                onClick={() => setOpenModal(true)}
                fullWidth
              >
                <div className={classes.btnBackground}>
                  <SVGGiftIcon />
                </div>
                Claim Gift
              </Button>
              <Button
                className={classes.hamburguerButton}
                variant="text"
                onClick={() => intercomHelp()}
                fullWidth
              >
                <div className={classes.btnBackground}>
                  <SVGChat />
                </div>
                Help?
              </Button>

              <Button
                className={classes.hamburguerSendsparkButton}
                variant="contained"
                color="primary"
                onClick={() => redirectToSendspark(true, env)}
              >
                Sign up for Sendspark
              </Button>

              <Button
                className={classes.hamburguerSendsparkButton}
                variant="text"
                color="primary"
                onClick={() => redirectToSendspark(false, env)}
              >
                Go to Sendspark.com
                <SVGExternalLinkIcon color={theme.palette.primary.main} />
              </Button>
            </Grid>
          </Collapse>
          <div
            className={classes.contentContainer}
            style={{ display: isOpenHamburguerMenu && isMobile && !isTablet ? 'none' : 'block', overflow: 'scroll', paddingTop: 0 }}
          >
            {children}
          </div>
        </div>
        <div className={classes.footer}>
          <div className={classes.social}>
            <Button variant="text" onClick={() => shareLinkOnLinkedIn(env)}>
              <SVGLinkedinIcon width="25" height="25" />
            </Button>
            <Button variant="text" onClick={() => shareLinkOnTwitter(env)}>
              <SVGTwitterIcon width="25" height="25" />
            </Button>
            <Button variant="text" onClick={() => shareLinkOnFacebook(env)}>
              <SVGFbIcon width="25" height="25" />
            </Button>
            <Button variant="text" onClick={() => shareLinkOnMail(env)}>
              <SVGEmailIcon width="25" height="25" />
            </Button>
          </div>
          <RenderIf condition={isLaptop}>
            <div className={classes.copyright}>
              <Typography>© Copyright 2023 Sendspark, Inc.</Typography>
            </div>
          </RenderIf>
          <div className={classes.btnFooterRight}>
            <Button variant="text" onClick={() => setIsOpenFAQModal(true)}>
              <div className={classes.btnBackground}>
                <SVGHelpCircle />
              </div>
              <RenderIf condition={!isMobile || isTablet}>
                <>FAQ</>
              </RenderIf>
            </Button>
            <Button variant="text" onClick={() => setOpenModal(true)}>
              <div className={classes.btnBackground}>
                <SVGGiftIcon />
              </div>
              <RenderIf condition={!isMobile || isTablet}>
                <>Claim Gift</>
              </RenderIf>
            </Button>
            <Button variant="text" onClick={() => intercomHelp()}>
              <div className={classes.btnBackground}>
                <SVGChat />
              </div>
              <RenderIf condition={!isMobile || isTablet}>
                <>Help</>
              </RenderIf>
            </Button>
          </div>
        </div>
      </div>

      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        title="Claim Gift"
        description="Sendspark is not your average screen recorder... We give you superpowers for sales! Here’s a gift to help you start winning with personalized video at scale."
      >
        <>
          <div className={classes.codeInput}>
            <Typography>AISCRIPTSPARK50</Typography>
            <Button onClick={copyToClipboard}>
              <SVGCopyClipboard2 color={theme.palette.primary.main} />
            </Button>
          </div>
          <Button
            className={classes.redirectSendspark}
            variant="outlined"
            color="primary"
            onClick={() => redirectToSendspark(true, env)}
          >
            Get Started Free!
          </Button>
          <Typography className={classes.giftDescription}>
            Get 50% off your first month!
          </Typography>
        </>
      </Modal>
      <Faq isOpen={isOpenFAQModal} handleOnClose={setIsOpenFAQModal} />
    </div>
  );
};

export default Layout;
